import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { FD_CONFIG, useWidgetAllowed, type FdWidget } from '@/lib/freshdesk';

/**
 * SAB-916 — attach a VERIFIED identity to the Freshdesk web chat widget, and keep it alive.
 *
 * Support conversations here are about deposits, withdrawals and league disputes. An
 * asserted identity means a user can claim to be anyone in a conversation about someone
 * else's money, so the id is signed server-side and verified by Freshdesk rather than taken
 * from the browser's word.
 *
 * ⚠️ THIS IS THE `fdWidget` API, NOT `fcWidget`. Freshdesk's web chat widget and Freshchat
 * are different products with different globals. `user.clear({ force: true })` is Freshchat's;
 * the surface here is `user.get / create / update / clear() / isExists()`, plus
 * `init` / `reInit(config)` / `destroy()` / `isLoaded()` / `authenticate(token)` / `getToken()`.
 *
 * ⚠️ CORRECTION, AND THE REASON THIS FILE CHANGED. An earlier version of this comment said
 * `authenticate()` and `setExternalId()` "DO NOT EXIST here" and that "calling them would
 * throw and take the widget down". That was WRONG, and it was wrong in the place most likely
 * to stop the next person checking. Both exist. From the shipped bundle:
 *
 *     authenticate(e){ let t = () => { l.setJWTAuthToken(e),
 *                        l.postMessage({action:"authenticate_user", payload:e}) };
 *                      this.isLoaded() && t() }
 *
 * Enumerating `Object.keys(window.fdWidget)` at runtime is what settled it. A claim that a
 * third party's method is ABSENT needs the same evidence as a claim that it is present.
 *
 * ⚠️ THE TWO PATHS ARE NOT INTERCHANGEABLE — both halves of this were MEASURED on staging
 * against the real Freshdesk account, not reasoned about:
 *
 *   reInit(config)      BOOTSTRAPS a verified session. It issues POST /visitors/restore-by-jwt
 *                       and the visitor id switches from the anonymous one to the record keyed
 *                       to unique_external_id. It is destructive: it removes the iframe from
 *                       #fd_frame and re-runs init, so an OPEN CONVERSATION is torn down.
 *   authenticate(token) REFRESHES the credential on a session that is ALREADY JWT-based, with
 *                       no teardown. After a 401 on a lapsed token, the next send returned 200
 *                       and the widget re-delivered the message that had failed.
 *                       It CANNOT bootstrap: called on an anonymous widget it emitted no
 *                       restore-by-jwt at all, and the next message landed on the ANONYMOUS
 *                       visitor while every call returned without throwing.
 *
 * That asymmetry is the whole shape of this file: reInit once to establish identity, then
 * authenticate() to keep it, so a refresh never destroys a conversation in progress.
 *
 * reInit takes a FULL config, not a patch — hence the single exported FD_CONFIG in
 * lib/freshdesk.ts, so token/host/widgetId cannot drift between here and the loader. The
 * failure that guards against is a widget initialising under one widgetId and re-initialising
 * under another: two conversation histories for one user, and no error anywhere. #fd_frame
 * itself survives, so the CSS offsets are undisturbed and the launcher does not jump on login.
 *
 * (SAB-938 moved this config out of a `window.__FD_CONFIG` global set by index.html; the
 * widget is now loaded from React so that it can be gated on consent.)
 *
 * ⚠️ `authenticate()` IS A SILENT NO-OP IF THE WIDGET IS NOT LOADED. The guard is
 * `this.isLoaded() && t()` — no throw, no queue, no return value. Calling it a moment early
 * does nothing at all and the session simply lapses later with no trace, so every call below
 * goes through whenLoaded(), which gates on isLoaded() and is LOUD when it gives up.
 *
 * ⚠️ WHY REFRESH AT ALL — what expiry actually does, measured. Freshdesk enforces `exp`
 * server-side (401, code 1010 "JWT token is expired"). Mid-conversation the send is rejected
 * and the message gets a small `ic-unauthenticated` icon whose only explanation is a
 * hover tooltip — there is no hover on a phone — and the realtime channel drops, so agent
 * replies stop arriving too. It does NOT silently fall back to anonymous, which is the one
 * good thing about it. Refreshing before that happens is cheaper than a longer TTL, which
 * would only widen the window a leaked token is useful for without fixing the failure.
 *
 * ⚠️ ON FAILURE WE STAY ANONYMOUS, NEVER FALL BACK TO ASSERTED. If the signing call fails we
 * leave the widget unauthenticated rather than pushing an unverified id. Silently degrading
 * to asserted identity while an agent believes authentication is on is worse than having no
 * identity at all — the agent would trust a claim nothing checked. Same reasoning as the
 * SAB-929 sweep refusing to report success it cannot back.
 */

/** The widget arrives from a remote script on window.load and exposes no ready callback. */
const POLL_MS = 400;
const MAX_WAIT_MS = 30_000;

/**
 * Refresh at 80% of the token's ACTUAL remaining life, taken from the endpoint's own
 * `expires_at` rather than duplicating the server's TTL as a client constant — if the TTL is
 * ever retuned, this follows it instead of silently disagreeing with it.
 */
const REFRESH_FRACTION = 0.8;
/** Floor, purely so a pathologically short TTL cannot turn the refresh into a hot loop. At
 *  the shipped 900s TTL the 80% point is 720s, so this is never the binding constraint. */
const MIN_REFRESH_MS = 30_000;
const RETRY_MS = 60_000;
const MAX_RETRIES = 3;

const FreshdeskIdentity = () => {
  const { user } = useAuth();
  const uid = user?.id ?? null;
  /**
   * SAB-938: the widget only exists once consent is recorded, so identity must not run
   * before it. Reactive rather than a one-shot read — a visitor can log in first and accept
   * afterwards, and without re-running on that transition their chat would stay anonymous
   * for the rest of the session while the widget sat there looking authenticated.
   */
  const allowed = useWidgetAllowed();
  // The id we last authenticated. Distinguishes sign-OUT (a transition) from "logged out
  // right now", which is also true on first load for a visitor with nothing to clear.
  const lastAuthed = useRef<string | null>(null);

  useEffect(() => {
    // No consent, no widget, so nothing to attach an identity to. Returning here also
    // avoids minting a support token for a session that cannot use one.
    if (!allowed) return;
    if (uid === lastAuthed.current) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let expiresAt: number | null = null; // epoch seconds, from the endpoint
    let fullLifeMs = 0; // the token's life at mint, for the visibility threshold
    let retries = 0;
    const stopWaits: Array<() => void> = [];

    const clearTimer = () => { if (timer) { clearTimeout(timer); timer = null; } };

    /** Gate on isLoaded(), because authenticate() and reInit() both need a live widget. */
    const whenLoaded = (fn: (w: FdWidget) => void, ctx: string) => {
      if (window.fdWidget?.isLoaded?.()) { fn(window.fdWidget); return; }
      let waited = 0;
      const t = setInterval(() => {
        waited += POLL_MS;
        if (cancelled) { clearInterval(t); return; }
        if (window.fdWidget?.isLoaded?.()) { clearInterval(t); fn(window.fdWidget); return; }
        if (waited >= MAX_WAIT_MS) {
          clearInterval(t);
          // Loud on purpose: a silent no-op here is exactly how the identity would go
          // missing while everyone believed authentication was on.
          console.error(`[SAB-916] Freshdesk widget never reported isLoaded() — ${ctx} skipped`);
        }
      }, POLL_MS);
      stopWaits.push(() => clearInterval(t));
    };

    /** No body. The endpoint signs the CALLER's identity from their own Supabase JWT --
     *  passing a user id would rebuild the forgery hole this whole path closes. */
    const mint = async (): Promise<{ token: string; expiresAt: number } | null> => {
      try {
        const { data, error } = await supabase.functions.invoke('freshdesk-jwt');
        if (error) throw error;
        const body = data as { token?: string; expires_at?: number } | null;
        if (!body?.token) throw new Error('response carried no token');
        return { token: body.token, expiresAt: body.expires_at ?? 0 };
      } catch (e) {
        console.error('[SAB-916] Freshdesk token request failed:', e);
        return null;
      }
    };

    const scheduleRefresh = () => {
      clearTimer();
      if (expiresAt == null) return;
      const lifeMs = expiresAt * 1000 - Date.now();
      timer = setTimeout(
        () => { void refresh('scheduled'); },
        Math.max(MIN_REFRESH_MS, lifeMs * REFRESH_FRACTION),
      );
    };

    const refresh = async (reason: string) => {
      if (cancelled || !uid) return;
      const minted = await mint();
      if (cancelled) return;
      if (!minted) {
        // The OLD token is still valid until exp, so a failed refresh is not a reason to
        // tear the session down -- try again inside its remaining life, then give up loudly.
        clearTimer();
        if (retries++ < MAX_RETRIES) {
          console.error(`[SAB-916] Freshdesk token refresh (${reason}) failed — retrying`);
          timer = setTimeout(() => { void refresh('retry'); }, RETRY_MS);
        } else {
          console.error('[SAB-916] Freshdesk token refresh failed repeatedly — the verified session will lapse at exp');
        }
        return;
      }
      retries = 0;
      expiresAt = minted.expiresAt;
      fullLifeMs = minted.expiresAt * 1000 - Date.now();
      whenLoaded((w) => {
        try {
          // Non-destructive: keeps the open conversation. See the asymmetry note above.
          w.authenticate?.(minted.token);
          scheduleRefresh();
        } catch (e) {
          console.error('[SAB-916] Freshdesk authenticate() failed:', e);
        }
      }, `token refresh (${reason})`);
    };

    /**
     * A timer alone is not enough. A backgrounded or slept tab does not fire it on schedule,
     * and that is precisely the case this exists for: someone leaves a withdrawal
     * conversation open, comes back later, and types into a session whose token lapsed while
     * the machine was asleep. On wake, refresh if we are inside the last 20% of the life.
     */
    const onVisible = () => {
      if (cancelled || document.visibilityState !== 'visible' || expiresAt == null) return;
      const leftMs = expiresAt * 1000 - Date.now();
      if (leftMs <= fullLifeMs * (1 - REFRESH_FRACTION)) void refresh('tab visible');
    };

    if (!uid) {
      if (lastAuthed.current === null) return;
      lastAuthed.current = null;
      whenLoaded((w) => {
        try {
          // Clear the record, then re-init WITHOUT a token. clear() alone would drop the
          // user while leaving the widget in an authenticated configuration; the next
          // visitor on a shared device would inherit a verified session shell.
          w.user?.clear?.();
          w.reInit?.({ ...FD_CONFIG });
        } catch (e) {
          console.error('[SAB-916] Freshdesk sign-out re-init failed:', e);
        }
      }, 'sign-out re-init');
      return () => { cancelled = true; stopWaits.forEach((s) => s()); };
    }

    void (async () => {
      const minted = await mint();
      if (!minted || cancelled) {
        // Stay anonymous. Loud, because "support says authentication is on" while every
        // session is anonymous is the failure that would otherwise go unnoticed.
        if (!minted) console.error('[SAB-916] Freshdesk identity unavailable — staying ANONYMOUS');
        return;
      }
      expiresAt = minted.expiresAt;
      fullLifeMs = minted.expiresAt * 1000 - Date.now();
      whenLoaded((w) => {
        try {
          // reInit, not authenticate: only reInit bootstraps a verified session.
          w.reInit?.({ ...FD_CONFIG, jwtAuthToken: minted.token });
          lastAuthed.current = uid;
          scheduleRefresh();
        } catch (e) {
          console.error('[SAB-916] Freshdesk reInit failed — staying ANONYMOUS:', e);
        }
      }, 'initial verified init');
    })();

    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearTimer();
      stopWaits.forEach((s) => s());
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [uid, allowed]);

  return null;
};

export default FreshdeskIdentity;
